import { CaretLeftIcon, ExportIcon, StarIcon } from '@phosphor-icons/react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import Button from '@/src/components/core/Button'
import Picture from '@/src/components/elements/Picture'
import { Breadcrumb } from '@/src/components/flch/breadcrumbs'
import Breadcrumbs from '@/src/components/flch/components/Breadcrumbs'
import Container from '@/src/components/layout/Container'

function getAverageRating(
  reviews: FahrlehrerCh.TeacherProfileReview[]
): number | null {
  const values: number[] = []
  for (const review of reviews) {
    const r = review.ratings
    const fields = [
      r.progress,
      r.explanations,
      r.learningGoals,
      r.attention,
      r.patience,
      r.scheduling,
      r.cleanliness,
      r.punctuality,
    ]
    for (const v of fields) {
      if (v !== null) values.push(v)
    }
  }
  return values.length > 0
    ? values.reduce((s, v) => s + v, 0) / values.length
    : null
}

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

const QuoteIcon = () => (
  <svg
    width="39"
    height="30"
    viewBox="0 0 39 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="pointer-events-none absolute -top-3 left-2 -z-10 select-none"
  >
    <path
      opacity="0.5"
      d="M0 30V21.0867C0 18.5557 0.491596 15.9697 1.47479 13.3287C2.45798 10.6878 3.75525 8.19807 5.3666 5.8597C6.97794 3.52132 8.72584 1.56809 10.6103 0L18.312 4.58047C16.7826 7.00138 15.5263 9.53232 14.5431 12.1733C13.5872 14.8143 13.1092 17.7579 13.1092 21.0041V30H0ZM20.688 30V21.0867C20.688 18.5557 21.1796 15.9697 22.1628 13.3287C23.146 10.6878 24.4433 8.19807 26.0546 5.8597C27.666 3.52132 29.4139 1.56809 31.2983 0L39 4.58047C37.4706 7.00138 36.2143 9.53232 35.2311 12.1733C34.2752 14.8143 33.7973 17.7579 33.7973 21.0041V30H20.688Z"
      fill="#B5C1FF"
    />
  </svg>
)

const Header = ({
  name,
  avatar,
  shortDescription,
  shortSentence,
  profileTags,
  reviews,
  breadcrumbs,
  selectedLocation,
}: FahrlehrerCh.TeacherProfile & {
  selectedLocation: FahrlehrerCh.TeacherProfileLocation
  breadcrumbs: Breadcrumb[]
}) => {
  const router = useRouter()
  const origin = router.query.origin as string | undefined
  const avg = getAverageRating(reviews)

  const handleShare = async () => {
    try {
      await navigator.share({ title: name, url: window.location.href })
    } catch {}
  }

  profileTags = [
    'Geduldig',
    'Ruhige Atmosphäre',
    'Gute Erklärungen',
    'Direkt',
    'Flexibel',
  ]

  return (
    <div className="pt-15 md:bg-linear-to-b md:from-gray-50 md:to-gray-100 md:pt-21">
      <Container fullPageOnMobile className="md:pt-6 md:pb-10">
        <Breadcrumbs
          items={breadcrumbs}
          currentAsHeading={false}
          className="px-5 py-4 md:px-0 md:pt-0"
        />
        {origin && (
          <Link
            href={origin}
            className="hidden items-center gap-1 text-sm font-bold text-blue-700 md:flex"
          >
            <CaretLeftIcon size={20} />
            Zur Übersicht
          </Link>
        )}
        <div
          className={`flex flex-col md:flex-row md:items-start md:gap-6${origin ? 'md:mt-12' : ''}`}
        >
          <div className="relative aspect-6/4 w-full overflow-hidden md:aspect-auto md:h-48 md:w-48 md:shrink-0 md:rounded-full md:border-4 md:border-white md:shadow-sm">
            {avatar && (
              <Picture
                permalink={avatar.url}
                fill
                alt={`${name}, Fahrlehrperson in ${selectedLocation.name}`}
                className="object-cover"
                sizes="(min-width: 1024px) 192px, 100vw"
              />
            )}
            <div className="absolute inset-x-0 top-5 flex items-center px-5 md:hidden">
              {origin && (
                <Button
                  size="icon"
                  version="white-gray"
                  icon={<CaretLeftIcon />}
                  onClick={() => router.push(origin)}
                  className="shadow-md"
                  aria-label="Zurück"
                />
              )}
              <Button
                size="icon"
                version="white-gray"
                icon={<ExportIcon />}
                onClick={handleShare}
                className="ml-auto shadow-md"
                aria-label="Teilen"
              />
            </div>
          </div>
          <div className="relative z-10 -mt-4 flex-1 rounded-t-2xl bg-white px-5 pt-6 pb-8 md:mt-0 md:ml-6 md:rounded-none md:bg-transparent md:px-0 md:pt-0 md:pb-0">
            <h1 className="text-4xl font-black md:text-10xl">{name}</h1>
            {shortDescription && (
              <p className="mt-1 text-gray-600">{shortDescription}</p>
            )}
            {shortSentence && (
              <div className="relative mt-5">
                <QuoteIcon />
                <p className="text-sm leading-5">{shortSentence}</p>
              </div>
            )}
            {profileTags.length > 0 && (
              <div className="-mx-5 mt-4 flex gap-2 overflow-x-auto md:mx-0 md:flex-wrap md:overflow-x-visible">
                {profileTags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-lg bg-gray-200 px-3 py-1.5 text-sm whitespace-nowrap first:ml-5 last:mr-5 md:first:ml-0 md:last:mr-0"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-5 flex flex-col md:mt-6 md:flex-row md:items-center">
              {avg !== null && (
                <div className="order-last mt-4 flex justify-center md:order-first md:mt-0 md:justify-start">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <span className="font-bold">{fmt(avg)}/5</span>
                      <StarIcon
                        size={20}
                        weight="fill"
                        className="text-yellow-500"
                      />
                    </div>
                    <Link href="#reviews" className="underline">
                      {reviews.length}{' '}
                      {reviews.length === 1 ? 'Bewertung' : 'Bewertungen'}
                    </Link>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 md:ml-6">
                <Button
                  as={Link}
                  href="#booking"
                  version="blue"
                  className="flex-1 md:hidden"
                >
                  Probelektion buchen
                </Button>
                <Button
                  as={Link}
                  href="#booking"
                  version="blue"
                  size="small"
                  className="hidden flex-1 md:block md:flex-none"
                >
                  Probelektion buchen
                </Button>
                <Button
                  version="gray-outlined"
                  size="small"
                  icon={<ExportIcon />}
                  onClick={handleShare}
                  className="hidden md:flex"
                >
                  Teilen
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  )
}

export default Header
